/**
 * SAB-852: resolve the Supabase config, and decide when a missing env is fatal.
 *
 * Pure and exhaustively unit-tested, deliberately mirroring the SAB-577 shape
 * (`resolveActiveNflLeagueId`): the decision lives here where it can be tested against
 * every combination, and `client.ts` supplies `import.meta.env` at the edge. Vite
 * substitutes `import.meta.env.*` at build time, so a resolution written inline in
 * `client.ts` can only ever be observed in whichever mode the test runner happens to be.
 *
 * ── THE DEFECT THIS EXISTS TO CLOSE ─────────────────────────────────────────
 *
 * The old code was:
 *
 *   export const SUPABASE_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID ?? "fndnsflniialrkopxcnr";
 *   if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) throw ...
 *
 * `.env.local` is gitignored, so `git worktree add` never copies it. A dev server in a
 * fresh worktree resolved every var to `undefined`, `??` handed back the hardcodes, and
 * the app talked to PRODUCTION. The guard below it could not fire: `??` had already
 * populated both constants, so the condition was unreachable on unset.
 *
 * The failure mode is what makes it expensive: **it presents as `Invalid login
 * credentials`**, indistinguishable from a wrong password, because a staging account
 * genuinely does not exist in prod. It has cost multiple debugging cycles. Nothing was
 * harmed only because the operations that failed were reads; a dev server that WRITES
 * would have written to production, and every RLS-protected write a developer is
 * authorised to make against their own prod account would have succeeded silently.
 *
 * ── WHAT THE FALLBACK IS FOR, AND WHY IT SURVIVES ───────────────────────────
 *
 * Traced to 217506d (2026-03-12), which replaced a generated `const SUPABASE_URL =
 * import.meta.env.VITE_SUPABASE_URL` with a hardcode to stop the file being regenerated
 * and shipping a build that pointed nowhere. The "automatically generated" marker is gone
 * and no script writes this file today -- but `VITE_SUPABASE_*` is set in NO CI workflow,
 * so a production build still depends entirely on Vercel's project env. If that env were
 * ever cleared, this fallback is the only thing keeping production pointed at production.
 *
 * So the fallback is NOT removed and NOT weakened. It is keyed to the context it was
 * always about:
 *
 *   production build  ->  fallback applies. The guarantee is untouched.
 *   any dev context   ->  no fallback, and unset is FATAL.
 *
 * It deliberately does NOT fall back to staging. If Vercel's env were cleared, a staging
 * fallback would make production silently serve against staging -- which *appears to
 * work*, and is therefore worse than either failing loudly or landing on prod.
 *
 * ── `vite build --mode development` (`npm run build:dev`) ───────────────────
 *
 * Decided deliberately, and NOT on the reasoning you would expect. The assumption going
 * in was that `--mode development` sets PROD=false, so `build:dev` would both lose the
 * fallback and trip the throw at once. **That is false.** Measured, by probing all three
 * values in an actual bundle from each script:
 *
 *   npm run build      MODE=production   DEV=false  PROD=true
 *   npm run build:dev  MODE=development  DEV=false  PROD=true    <-- PROD is still TRUE
 *   vite (dev server)  MODE=development  DEV=true   PROD=false
 *
 * Vite ties DEV/PROD to whether this is a build at all (NODE_ENV), not to `--mode`. So
 * `import.meta.env.PROD` alone would silently treat `build:dev` as a production build and
 * hand back the prod fallback -- exactly the class of failure this file exists to close,
 * just moved to a different entry point. The conflict does not exist; the SILENCE does.
 *
 * Hence the caller passes `PROD && MODE !== 'development'`. A bundle that declares itself
 * a development artifact gets development semantics: no fallback, and unset is fatal.
 *
 * Note it excludes the literal mode 'development' only. A future `--mode staging` deploy
 * would still be a production build and keep the guarantee, which is the right default --
 * the rule is "development artifacts are not trusted with the prod fallback", not "only
 * mode=production is". Nothing references `build:dev` today (not CI, and not Vercel, whose
 * build command is `npm run build`), so this changes no working pipeline.
 */

/** The production project. The fallback of last resort for a PRODUCTION BUILD only. */
export const PROD_PROJECT_ID = 'fndnsflniialrkopxcnr';
export const PROD_PUBLISHABLE_KEY = 'sb_publishable_X_EdMfqBKXtRowTQAsk6vQ_IcWtjPxx';

/** Only the fields we read. Values are `string | undefined`; Vite gives unset as undefined. */
export interface SupabaseEnv {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_PROJECT_ID?: string;
  VITE_SUPABASE_PUBLISHABLE_KEY?: string;
}

export interface ResolvedSupabaseConfig {
  projectId: string;
  url: string;
  publishableKey: string;
  /**
   * Where the values came from. Drives the boot log, and is the thing that makes a
   * cleared Vercel env visible in the field instead of silent.
   */
  source: 'env' | 'fallback';
}

/** Thrown for a dev context with no env. A distinct type so tests assert on more than a string. */
export class SupabaseConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SupabaseConfigError';
  }
}

/**
 * @param env               `import.meta.env`, or any object with the three fields.
 * @param isProductionBuild whether the prod fallback is allowed to apply. The caller
 *                          computes it as `PROD && MODE !== 'development'` -- see the
 *                          `build:dev` section above for why `PROD` alone is not enough.
 */
export function resolveSupabaseConfig(
  env: SupabaseEnv,
  isProductionBuild: boolean,
): ResolvedSupabaseConfig {
  const envUrl = env.VITE_SUPABASE_URL;
  const envProjectId = env.VITE_SUPABASE_PROJECT_ID;
  const envKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;

  // A URL can legitimately be derived from the project id alone -- that is how the
  // original code worked, and a config setting PROJECT_ID + KEY but not URL is valid.
  // So "unset" means neither of the two is present, not merely that URL is absent.
  const canResolveUrl = envUrl !== undefined || envProjectId !== undefined;
  const canResolveKey = envKey !== undefined;

  if (!isProductionBuild && (!canResolveUrl || !canResolveKey)) {
    const missing = [
      canResolveUrl ? null : 'VITE_SUPABASE_URL (or VITE_SUPABASE_PROJECT_ID)',
      canResolveKey ? null : 'VITE_SUPABASE_PUBLISHABLE_KEY',
    ].filter(Boolean);

    throw new SupabaseConfigError(
      `[supabase/client] Supabase env is not set: ${missing.join(', ')}.\n\n` +
        'Refusing to fall back to the production project. Before SAB-852 this silently ' +
        'connected the app to PRODUCTION, which surfaces as "Invalid login credentials" ' +
        'and is indistinguishable from a wrong password.\n\n' +
        '.env.local is gitignored, so `git worktree add` does not copy it. In a new ' +
        'worktree:\n' +
        '    cp /path/to/main/checkout/.env.local .\n\n' +
        'The production fallback still exists and is untouched, but it applies only to a ' +
        'production build (import.meta.env.PROD), never to a dev server or ' +
        '`npm run build:dev`.',
    );
  }

  // Past this point either the env is present, or this is a production build and the
  // fallback is doing exactly the job 217506d added it for.
  //
  // `??`, not `||`, throughout: an explicit-empty value (VITE_SUPABASE_URL="") is a
  // deliberate act and must reach the emptiness assertion in client.ts rather than be
  // quietly replaced by the prod hardcode. `||` would swallow it. This is the operator
  // choice the original file documented, kept, and now applied consistently everywhere
  // a Supabase host is resolved.
  const projectId = envProjectId ?? PROD_PROJECT_ID;
  const url = envUrl ?? `https://${projectId}.supabase.co`;
  const publishableKey = envKey ?? PROD_PUBLISHABLE_KEY;

  return {
    projectId,
    url,
    publishableKey,
    source: canResolveUrl && canResolveKey ? 'env' : 'fallback',
  };
}

/**
 * The boot line. Prints the resolved project REF, never the URL.
 *
 * The URL is built as `https://${projectId}.supabase.co`, a template literal, so the
 * host that a build actually talks to NEVER appears as a literal string in the bundle.
 * Grepping the bundle for `https://*.supabase.co` therefore returns only incidental
 * literals and misses the real target entirely -- that is precisely what defeated three
 * separate greps while diagnosing this, and pointed at a long-dead project ref hardcoded
 * elsewhere in the app. The ref is the greppable, comparable fact; the URL is not.
 *
 * `source` is the half that matters in production: a cleared Vercel env would still
 * resolve to prod (by design), and `source=fallback` is the only thing that would say so.
 */
export function supabaseBootLine(config: ResolvedSupabaseConfig): string {
  return `[supabase] project=${config.projectId} source=${config.source}`;
}
